/**
 * EL SALUDO de la home ("Buenas noches, Maxi / ¿Qué quisieras crear hoy?") —
 * ADR-2026-09-21-el-saludo-tambien-va-en-el-telefono (ajusta
 * ADR-2026-09-21-el-saludo-se-centra-arriba-del-switch).
 *
 * Es UN componente y no un nodo armado adentro de `HomeMinimal` porque ahora vive en
 * TRES lugares: la fila del encabezado de la home de siempre, el centro del hero del
 * Agente en escritorio, y el hero del Agente en el teléfono. Los dos primeros los monta
 * `HomeMinimal`; el tercero lo monta `InterfazV2`, que no tiene el contexto de cuenta del
 * shell de escritorio y trae el nombre por su cuenta (`profiles.name_short`). Con el
 * saludo escrito adentro de `HomeMinimal` no había forma de que el teléfono lo usara sin
 * copiarlo, y una copia del mismo título se desincroniza al primer retoque.
 *
 * El nombre entra por prop y no se resuelve acá: cada pantalla lo saca de donde lo tiene
 * (el shell en escritorio, la consulta de perfil en el teléfono) y este componente sólo
 * decide cómo se ve.
 */
// La franja horaria sale de `contenidoAtlas`, el mismo lugar del que salía cuando el
// saludo vivía en `HomeMinimal`: "Buenos días" / "Buenas tardes" / "Buenas noches" con la
// hora de Argentina.
import { saludo as saludoSegunLaHora } from '../../estilos/contenidoAtlas';

/**
 * Los tres tamaños, uno por lugar. El salto entre las dos líneas es siempre el mismo
 * gesto —el saludo es el título y la pregunta su bajada— pero el par cambia con el ancho:
 *
 * - `encabezado` (32/26): la fila del encabezado de la home de siempre, donde comparte
 *   renglón con los accesos de cuenta y más grande los empuja.
 * - `hero` (38/28): centrado en el hero del Agente en escritorio, donde es el único
 *   título de la pantalla y a 32 quedaba chico para lo que encabeza.
 * - `telefono` (26/20): el hero en mobile. A 32 entraba justo —"Buenas noches, Maxi" son
 *   319px en una columna de 350— pero ocupaba casi todo el ancho y empujaba la caja
 *   abajo del pliegue; a 26 el bloque respira y el título sigue mandando.
 *
 * El `mt` de la pregunta es el aire ENTRE LOS DOS RENGLONES, y va acá y no en el
 * `leading` del `h1` por una razón: el `leading` afecta a los dos renglones por igual —
 * también adentro del saludo si algún día se parte en dos líneas— y lo que hay que
 * separar es una línea de la otra. Crece con el tamaño (1.5 en el encabezado, 2 en el
 * hero y en el teléfono) porque a `leading-[1.2]` los dos renglones se tocaban y se leían
 * como un bloque en vez de como un título con su bajada.
 */
const TAMANOS = {
  encabezado: { saludo: 'text-[32px]', pregunta: 'text-[26px] mt-1.5' },
  hero: { saludo: 'text-[38px]', pregunta: 'text-[28px] mt-2' },
  telefono: { saludo: 'text-[26px]', pregunta: 'text-[20px] mt-2' },
} as const;

export interface SaludoDeLaHomeProps {
  /** El nombre de pila ya resuelto. Vacío o `null` → sólo "Buenas noches". */
  nombre?: string | null;
  /** Dónde vive el saludo. Ver `TAMANOS`. */
  tamano?: keyof typeof TAMANOS;
  /** La segunda línea. Por defecto, "¿Qué quisieras crear hoy?"; `null`, sin segunda línea. */
  pregunta?: string | null;
}

export function SaludoDeLaHome({ nombre, tamano = 'encabezado', pregunta = '¿Qué quisieras crear hoy?' }: SaludoDeLaHomeProps) {
  const medidas = TAMANOS[tamano];
  return (
    // EL TAMAÑO VA EN LOS DOS `span` Y NO EN EL `h1`, y no es una preferencia de estilo:
    // `.shell-seccion h1` (index.css) fija `font-size: 32px !important` para que las
    // pantallas hospedadas en el shell abran todas con el mismo título, y con
    // `!important` le gana a cualquier clase de Tailwind que se le ponga al `h1` — un
    // `text-[38px]` ahí no hace nada. Esa regla apunta al `h1` y no a sus hijos, así que
    // un `span` con su propio tamaño sí manda.
    <h1 className="leading-[1.2] tracking-[-0.02em]">
      {/* `font-[700]` y no `font-bold`: en este proyecto `.font-bold` vale **600** (la
          negrita de la tipografía de marca), y el 700 que la primera línea tenía venía
          del `font-weight: 700 !important` de `.shell-seccion h1`, que deja de
          alcanzarla ahora que el estilo vive en el `span`. Con `font-bold` las dos
          líneas quedaban en el mismo peso. */}
      <span className={`block font-[700] ${medidas.saludo}`}>
        {saludoSegunLaHora()}
        {nombre && `, ${nombre}`}
      </span>
      {/* La bajada siempre baja un escalón respecto del saludo: son el título y su
          bajada, y con tamaños parecidos se leían como dos títulos del mismo peso. */}
      {pregunta !== null && (
        <span className={`block font-[600] text-black/40 ${medidas.pregunta}`}>
          {pregunta}
        </span>
      )}
    </h1>
  );
}

export default SaludoDeLaHome;
